import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useChatContext, type ChatTarget } from '@/contexts/chat-context';
import { useAuth } from '@/contexts/integrated-auth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useVisualViewportHeight, useKeyboardOffset } from '@/hooks/use-keyboard-visible';
import { LeagueChatTab } from './league-chat-tab';
import { DailyRoomChat } from '../daily/daily-room-chat';
import { DailyLobbyChat } from '../daily/daily-lobby-chat';
import { SceneChatView } from './scene-chat-view';
import { CommunityChatView } from './community-chat-view';
import { ChatHub } from './chat-hub';
import { ChevronLeft, Trophy } from 'lucide-react';
import { PersonalMutesPanel } from './personal-mutes-panel';

type ChatUser = {
  id: string;
  username?: string | null;
  firstName?: string | null;
  displayName?: string | null;
  avatarImageUrl?: string | null;
} | null;

export function GlobalChatSheet() {
  const { isChatOpen, closeChat, chatTarget, openedFromHub, goBackToHub } = useChatContext();
  const { user, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const visualViewportHeight = useVisualViewportHeight();
  const keyboardOffset = useKeyboardOffset();

  if (!isAuthenticated) return null;

  // Back button: if drilled in from hub, go back to hub; otherwise close sheet
  const handleBack = openedFromHub && chatTarget.type !== 'hub' ? goBackToHub : closeChat;

  // On native iOS with Keyboard resize: "none", the webview stays full-size
  // and the keyboard overlays on top. We need to:
  // 1. Set the sheet height to the visual viewport height (above keyboard)
  // 2. Offset the sheet bottom by the keyboard height so it sits above the keyboard
  const nativeStyle = isMobile && visualViewportHeight
    ? { height: `${visualViewportHeight}px`, bottom: keyboardOffset > 0 ? `${keyboardOffset}px` : undefined }
    : undefined;

  return (
    <Sheet open={isChatOpen} onOpenChange={(open) => { if (!open) closeChat(); }}>
      <SheetContent
        side="bottom"
        className={`p-0 flex flex-col ${isMobile && !visualViewportHeight ? 'h-[100dvh]' : !isMobile ? 'h-[70vh]' : ''}`}
        style={nativeStyle}
        hideCloseButton
      >
        <SheetTitle className="sr-only">Chat</SheetTitle>
        <ChatSheetHeader target={chatTarget} onBack={handleBack} />
        <div className="flex-1 overflow-hidden">
          <ChatContent target={chatTarget} user={user} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ChatSheetHeader({ target, onBack }: { target: ChatTarget; onBack: () => void }) {
  const isMobile = useIsMobile();

  const title = getChatTitle(target);
  const isLeague = target.type === 'league';
  const themeColor = isLeague ? target.themeColor : undefined;
  const headerImage = isLeague ? target.headerImageUrl : undefined;

  return (
    <div
      className="flex items-center justify-between px-4 pb-3 border-b shrink-0"
      style={{
        paddingTop: isMobile ? 'max(12px, env(safe-area-inset-top, 12px))' : '12px',
        ...(themeColor ? {
          background: `linear-gradient(135deg, ${themeColor}18, ${themeColor}08)`,
          borderBottomColor: `${themeColor}30`,
        } : {}),
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onBack}
          className="p-1 -ml-1 rounded-full hover:bg-muted focus:outline-none active:bg-muted shrink-0"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        {isLeague && (
          headerImage ? (
            <img src={headerImage} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: themeColor || '#5D3FD3' }}
            >
              <Trophy className="h-4 w-4 text-white" />
            </div>
          )
        )}
        <h2 className="font-semibold text-lg truncate">{title}</h2>
      </div>
      {target.type !== 'hub' && <PersonalMutesPanel />}
    </div>
  );
}

function getChatTitle(target: ChatTarget): string {
  switch (target.type) {
    case 'league':
      return target.leagueName || 'League Chat';
    case 'daily-room':
      return target.roomName || 'Group Chat';
    case 'daily-lobby':
      return 'Lobby Chat';
    case 'scene':
      return target.sceneName ? `${target.sceneName} Chat` : 'Scene Chat';
    case 'community':
      return target.channelName || 'Community Room';
    case 'hub':
      return 'Chats';
  }
}

function ChatContent({ target, user }: { target: ChatTarget; user: ChatUser }) {
  switch (target.type) {
    case 'league':
      return (
        <LeagueChatTab
          leagueId={target.leagueId}
          isOwner={target.isOwner ?? false}
          chatEnabled={target.chatEnabled ?? true}
          chatImagesAllowed={target.chatImagesAllowed ?? true}
          fullScreen
        />
      );

    case 'daily-room':
      return (
        <DailyRoomChat
          roomId={target.roomId}
          roomName={target.roomName}
          userId={user?.id || null}
          username={user?.username || user?.displayName || null}
          avatarUrl={user?.avatarImageUrl || null}
          fullScreen
        />
      );

    case 'daily-lobby':
      return (
        <DailyLobbyChat
          roundId={target.roundId}
          userId={user?.id || null}
          username={user?.username || user?.displayName || null}
          avatarUrl={user?.avatarImageUrl || null}
          fullScreen
        />
      );

    case 'scene':
      return (
        <SceneChatView
          sceneId={target.sceneId}
          isMember={target.isMember ?? false}
        />
      );

    case 'community':
      return (
        <CommunityChatView
          channelId={target.channelId}
          channelName={target.channelName}
          categoryId={target.categoryId}
          categoryType={target.categoryType}
          isMember={target.isMember ?? false}
        />
      );

    case 'hub':
      return <ChatHub />;
  }
}
